import { StudentAvatar } from '@/components/shared/StudentAvatar/StudentAvatar';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useAuthStore } from '@/stores/authStore';
import type { Appointment } from '@/types/scheduling';
import type { Student } from '@/types/student';
import { Calendar } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RecurringBadge } from '../RecurringBadge/RecurringBadge';
import { SessionItemDropdown } from '../SessionItemDropdown/SessionItemDropdown';

interface MultipleSessionsListItemProps {
  sessionDate: {
    date: Date;
    appointments: Appointment[];
  };
}

export const MultipleSessionsListItem = ({ sessionDate }: MultipleSessionsListItemProps) => {
  const { format } = useTimeFor('sessionTime');

  return (
    <li>
      <div className='mb-2 flex gap-3 py-2.5'>
        <Calendar size={20} aria-hidden className='stroke-text-default-secondary' />
        <h3 className='text-body-m-semibold'>
          <time dateTime={new Date(sessionDate.date).toISOString()}>{format(sessionDate.date, 'MMMM do, h:mm a')}</time>
        </h3>
      </div>
      <ul className='bg-fill-background-medium desktop:space-y-2 space-y-5 rounded-lg p-4'>
        {sessionDate.appointments.map((appointment) => {
          return <SessionListItem key={appointment.appointment_id} appointment={appointment} />;
        })}
      </ul>
    </li>
  );
};

interface SessionListItemProps {
  appointment: Appointment;
}

const SessionListItem = ({ appointment }: SessionListItemProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'yourSessions' });
  const { userData, studentsBgColors } = useAuthStore();
  const sessionDropdownRef = useRef<HTMLButtonElement>(null);

  const student = userData?.students.find((s: Student) => s.id === appointment.student_id);

  return (
    <li className='flex justify-between'>
      <div className='flex items-center gap-2.5'>
        <StudentAvatar
          student={{
            firstName: student?.firstName ?? 'Unknown',
            lastName: student?.lastName ?? 'Unknown',
            bgColor: student ? (studentsBgColors.get(student.id) ?? '') : '',
          }}
          className='max-desktop:self-start'
        />
        <div className='max-desktop:flex-col desktop:gap-2.5 flex'>
          {student?.firstName}
          <div className='flex items-center gap-1.5'>
            <span className='text-text-default-secondary'>
              {t(`deliveryType.${appointment.delivery_type}`)} - {appointment.duration_minutes} {t('minutes')}
            </span>
            {appointment.session_type_enum === 'private' && (
              <>
                <div className='bg-text-default-secondary size-[3px] rounded-full' />
                <span className='text-text-default-secondary'>{t('private')}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center gap-2'>
        {appointment.recurring_schedule && <RecurringBadge />}
        <SessionItemDropdown appointment={appointment} openButtonRef={sessionDropdownRef} />
      </div>
    </li>
  );
};
